import React, {FC} from 'react';
import {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';

dayjs.extend(utc);

const LocalTimeRow = styled.div`
  font-size: 12px;
  color: #999999;
  padding-left: 4px;
  line-height: 1.4;
`;

interface ILocalTimeDisplayProps {
  utcDate: Dayjs | null;
  airportOffset: number | null | undefined;
  prefix?: string; // Префикс перед временем, например "Local: "
  className?: string;
}

/**
 * Компонент для отображения локального времени аэропорта на основе UTC времени.
 * Всегда отображает локальную дату и время.
 * 
 * @param utcDate - UTC дата в формате Dayjs
 * @param airportOffset - Смещение часового пояса аэропорта в минутах
 * @param prefix - Префикс перед временем (по умолчанию "Local: ")
 */
export const LocalTimeDisplay: FC<ILocalTimeDisplayProps> = ({
  utcDate,
  airportOffset,
  prefix = "Local: ",
  className
}) => {
  // Вычисление локального времени для аэропорта на основе UTC времени
  // Всегда отображает локальную дату и время
  const getLocalTimeForAirport = (utcDate: Dayjs | null, airportOffset: number | null | undefined): string => {
    if (!utcDate || airportOffset === null || airportOffset === undefined) {
      return "";
    }
    
    // Проверяем, является ли utcDate объектом Dayjs
    // Проверяем наличие методов Dayjs
    if (typeof utcDate.isUTC !== 'function' || typeof utcDate.year !== 'function') {
      return "";
    }
    
    // Получаем UTC момент - если дата не в UTC, интерпретируем её как UTC
    // (так как в форме указано UTC, пользователь вводит UTC время)
    let utcMoment: Dayjs;
    if (utcDate.isUTC()) {
      utcMoment = utcDate;
    } else {
      // Если дата не в UTC, создаем UTC дату из её компонентов
      // Это важно, чтобы не учитывать локальный часовой пояс браузера
      // Используем форматирование строки и парсинг как UTC
      const dateString = `${utcDate.year()}-${String(utcDate.month() + 1).padStart(2, '0')}-${String(utcDate.date()).padStart(2, '0')} ${String(utcDate.hour()).padStart(2, '0')}:${String(utcDate.minute()).padStart(2, '0')}:${String(utcDate.second()).padStart(2, '0')}`;
      utcMoment = dayjs.utc(dateString, 'YYYY-MM-DD HH:mm:ss');
    }
    
    // Конвертируем UTC время в локальное время аэропорта
    // airportOffset в минутах, нужно применить к UTC времени
    const localDate = utcMoment.add(airportOffset, 'minute');
    
    // Форматируем используя UTC компоненты, чтобы избежать влияния локального часового пояса браузера
    // Это важно, так как localDate все еще в UTC режиме, но мы хотим отобразить время как есть
    const pad = (num: number) => String(num).padStart(2, '0');
    const day = pad(localDate.utc().date());
    const month = pad(localDate.utc().month() + 1);
    const year = String(localDate.utc().year()).slice(-2);
    const hours = pad(localDate.utc().hour());
    const minutes = pad(localDate.utc().minute());
    
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const localTime = getLocalTimeForAirport(utcDate, airportOffset);

  if (!localTime) {
    return null;
  }

  return (
    <LocalTimeRow className={className}>
      {prefix}{localTime}
    </LocalTimeRow>
  );
};

